/**
 * gstDataSource.ts — WatermelonDB → GstInvoiceInput bridge.
 *
 * Loads a store's outward supplies (SALE + CREDIT_NOTE) for a calendar month
 * and normalises them into the plain objects `buildGstReturns` consumes. Keeps
 * all DB access in one place so the aggregation logic stays pure & testable.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Invoice, LineItem, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Customer } from '@/db/models/Customers';
import { loadStoreGstIdentity } from '@/gst/storeIdentity';

// Re-exported for existing consumers; the implementation now lives outside
// reports/, since billing and purchasing need the same fact.
export { loadStoreGstIdentity };
export type { StoreGstIdentity } from '@/gst/storeIdentity';
import type { GstInvoiceInput } from './gstReturns';

export interface GstMonthSource {
  storeName: string;
  gstin: string | null;
  homeStateCode: string | null;
  invoices: GstInvoiceInput[];
}

/** Load a store's GST source data for the given 0-indexed month/year. */
export async function loadGstMonth(
  storeId: string,
  year: number,
  month: number,
): Promise<GstMonthSource> {
  const monthStart = new Date(year, month, 1).getTime();
  const monthEnd = new Date(year, month + 1, 1).getTime();

  const { storeName, gstin, homeStateCode } = await loadStoreGstIdentity(storeId);

  // Voided invoices ARE loaded — the builder excludes them from every value
  // aggregation but reports them as cancelled documents in Table 13. Filtering
  // them out here would make a voided serial look like a missing number.
  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('type', Q.oneOf(['SALE', 'CREDIT_NOTE'])),
    // A DRAFT is not an issued document — it must never enter a GST return, not
    // even Table 13. Nothing currently creates a draft invoice, but the return
    // must contain only issued documents regardless of what writes them.
    Q.where('status', Q.notEq('DRAFT')),
    Q.where('created_at', Q.gte(monthStart)),
    Q.where('created_at', Q.lt(monthEnd)),
  ).fetch();

  const invoiceIds = invoices.map((i) => i.id);
  const lines = await fetchInChunks(invoiceIds, (batch) =>
    database.get<LineItem>('line_items').query(
      Q.where('invoice_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const linesByInvoice = new Map<string, LineItem[]>();
  for (const li of lines) {
    const arr = linesByInvoice.get(li.invoiceId) ?? [];
    arr.push(li);
    linesByInvoice.set(li.invoiceId, arr);
  }

  const custIds = [...new Set(invoices.map((i) => i.customerId).filter(Boolean))] as string[];
  const customers = await fetchInChunks(custIds, (batch) =>
    database.get<Customer>('customers').query(Q.where('id', Q.oneOf(batch))).fetch(),
  );
  const custMap = new Map<string, Customer>();
  for (const c of customers) custMap.set(c.id, c);

  const normalized: GstInvoiceInput[] = invoices.map((inv) => {
    const c = inv.customerId ? custMap.get(inv.customerId) : null;
    return {
      isCancelled: VOIDED_INVOICE_STATUSES.includes(inv.status),
      invoiceNumber: inv.invoiceNumber,
      invoiceDate: inv.createdAt,
      type: inv.type,
      isInterState: inv.isInterState,
      customer: c ? { name: c.name, gstin: c.gstin, state: c.state } : null,
      taxableAmount: inv.taxableAmount,
      cgstAmount: inv.cgstAmount,
      sgstAmount: inv.sgstAmount,
      igstAmount: inv.igstAmount,
      cessAmount: inv.cessAmount,
      totalTax: inv.totalTax,
      totalAmount: inv.totalAmount,
      roundOff: inv.roundOff,
      lines: (linesByInvoice.get(inv.id) ?? []).map((li) => ({
        hsnCode: li.hsnCode,
        description: li.description,
        quantity: li.quantity,
        unit: li.unit,
        taxableAmount: li.taxableAmount,
        gstRate: li.gstRate,
        cessRate: li.cessRate,
        cgstAmount: li.cgstAmount,
        sgstAmount: li.sgstAmount,
        igstAmount: li.igstAmount,
        cessAmount: li.cessAmount,
        totalAmount: li.totalAmount,
      })),
    };
  });

  return {
    storeName,
    gstin,
    homeStateCode,
    invoices: normalized,
  };
}
